import { Component, createRef } from 'react'
import {
    Badge,
    Card,
    CardBody,
    Col,
    Row,
} from 'reactstrap'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import { LoadingSpinner } from '../../@paco/components'
import { doesPeriodOverlapWithLockedPayrollPeriod } from '../../@paco/entities/PayrollPeriod/PayrollPeriodHelpers'
import { transformToPeriod } from '../../@paco/entities/Period/PeriodTransformers'
import { transformLegacyRoleToRole } from '../../@paco/entities/Role/RoleTransformers'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'

import { TRACK_TYPE_REVIEW } from '../../constants'
import { getIconById, getObjProperty, getUserFullName } from '../../helpers'
import { convertDateToTime } from '../../helpers/date'
import { getPermissionToEdit } from '../../helpers/permissions/getPermissionToAction'
import { translate } from '../../helpers/translations/translator'
import getPermissionToApproveTrack from '../../pages/Tracks/helpers/getPermissionToApproveTrack'
import { transformBillableTimeToHourMinutes } from '../../services/TrackService/getTotalBillableTimeFromTracks'
import ButtonWithTooltip from '../ButtonWithTooltip/ButtonWithTooltip'
import InfoCircle from '../InfoCircle/InfoCircle'
import TrackDurationTooLongWarning from '../TrackDurationTooLongWarning/TrackDurationTooLongWarning'
import UserPageLink from '../UserPageLink/UserPageLink'
import { getTrackCardApproveButtonTooltipLabel, getTrackCardEditButtonLabelTooltipLabel } from './helpers'
import InfoCircles from './subcomponents/InfoCircles/InfoCircles'

import './TrackCard.scss'

class TrackCard extends Component {
    constructor(props) {
        super(props)
        this.element = createRef()
    }

    onApproveClick = (id) => {
        this.props.onApproveClick(id)
    }

    render() {
        const {
            track,
            permissions,
            currentUserRole,
            enableFeedback,
            currentUserId,
            payrollPeriods = [],
            showAbnormalTrackDurationWarning = false,
            loading = false,
        } = this.props
        const {
            deviationRemark,
            unplannedRemark,
            owner,
            shift,
        } = track
        const userId = getObjProperty(track, 'owner.id')
        const department = getObjProperty(track, 'department.name')
        const group = getObjProperty(track, 'department.group.name')
        const shiftPlannings = getObjProperty(track, 'shift.shiftPlannings') || []
        const shiftPlanning = shiftPlannings
            .find(planning => planning.user && planning.user.id === userId)
        const shiftTime = shift && `${convertDateToTime(shift.period.start)} - ${convertDateToTime(shift.period.end)}`
        const shiftPlanningsTime = shiftPlanning && `${convertDateToTime(shiftPlanning.period.start)} - ${convertDateToTime(shiftPlanning.period.end)}`
        const shiftsLabel = shiftPlanningsTime || shiftTime
        const canViewSatisfaction = enableFeedback && checkPermission(permissions, 'view-all-feedback-ratings')
        const canViewSatisfactionFeedback = enableFeedback && checkPermission(permissions, 'view-all-feedback')
        const name = getUserFullName(owner)
        const legacyTrackOwnerRoles = getObjProperty(track, 'owner.roles') || []
        const trackOwnerRoles = legacyTrackOwnerRoles.map(transformLegacyRoleToRole)
        // When roles of track owner are missing then default to true. https://endeavour-nl.atlassian.net/browse/VDV-1038
        const canEditTrack = trackOwnerRoles.length ? getPermissionToEdit(currentUserRole, trackOwnerRoles, permissions, 'tracks') : true
        const payrollPeriodError = doesPeriodOverlapWithLockedPayrollPeriod(
            transformToPeriod(new Date(track.checkInOutPeriod.start), new Date(track.checkInOutPeriod.end)),
            payrollPeriods,
        )
        const canApproveTrack = getPermissionToApproveTrack(currentUserRole, trackOwnerRoles, permissions, userId, currentUserId)
        const acceptButtonTooltipText = getTrackCardApproveButtonTooltipLabel(payrollPeriodError, ! department)
        const editButtonTooltipLabel = getTrackCardEditButtonLabelTooltipLabel(payrollPeriodError)
        const billableEnd = track.billablePeriod.end

        return (
            <div ref={this.element} className={classNames(['track-card', { 'track-card--deleting': this.props.deleting }])}>
                <Card>
                    <CardBody className="py-2 track-card__body">
                        {loading && <LoadingSpinner isSmall />}

                        <div className="track-card__main">
                            <div className="d-flex track-card__title">
                                <UserPageLink className="track-card__name" name={name} id={track.owner.id} />
                                {department && (
                                    <Badge color="secondary">
                                        {`${group ? `${group} - ` : ''}${department}`}
                                    </Badge>
                                )}
                            </div>
                            <div className="track-card__info">
                                <Row>
                                    <Col className="mb-1">
                                        <div className="d-flex">
                                            {shiftsLabel && (
                                                <div className="mr-2 pr-1">
                                                    <div className="font-weight-semi-bold">{translate('common.shift')}</div>
                                                    <div>
                                                        {shiftsLabel}
                                                    </div>
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-weight-semi-bold">{translate('common.track')}</div>
                                                <div>{`${convertDateToTime(track.billablePeriod.start)} - ${billableEnd ? convertDateToTime(billableEnd) : translate('common.unknown')}`}</div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col className="mb-1">
                                        <div className="font-weight-semi-bold">{translate('common.deviation')}</div>
                                        <div>{`${track.deviation} ${translate('common.minutes')}`}</div>
                                    </Col>
                                    <Col className="mb-1">
                                        <div className="font-weight-semi-bold">{translate('pages.tracks.hoursWorked')}</div>
                                        <div>{transformBillableTimeToHourMinutes(track.totalBillableTime)}</div>
                                    </Col>
                                    <Col className="mb-1 flex-grow-2 d-flex">
                                        <InfoCircles
                                            track={track}
                                            canViewSatisfaction={canViewSatisfaction}
                                            canViewSatisfactionFeedback={canViewSatisfactionFeedback}
                                        />
                                        {(deviationRemark || unplannedRemark) && (
                                            <div className="ml-2 pl-1 d-flex">
                                                <InfoCircle
                                                    tooltip="Feedback"
                                                    icon={getIconById('balloon')}
                                                />
                                                <p className="pl-2 mt-1 mb-0 font-italic track-card__remark">{deviationRemark || unplannedRemark}</p>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                {showAbnormalTrackDurationWarning && (
                                    <Row>
                                        <Col>
                                            <TrackDurationTooLongWarning />
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        </div>
                        <div className="track-card__buttons">
                            <ButtonWithTooltip
                                disabled={! canEditTrack || payrollPeriodError}
                                tooltip={payrollPeriodError}
                                tooltipText={editButtonTooltipLabel}
                                color="link"
                                onClick={() => this.props.onReviewTrackClick(track, TRACK_TYPE_REVIEW)}
                                className="track-card__edit-button"
                            >
                                {translate('common.edit')}
                            </ButtonWithTooltip>
                            <ButtonWithTooltip
                                onClick={() => this.onApproveClick(track)}
                                tooltip={! department || payrollPeriodError}
                                tooltipText={acceptButtonTooltipText}
                                disabled={! canApproveTrack || ! department || payrollPeriodError}
                                className="track-card__button"
                            >
                                {translate('common.accept')}
                            </ButtonWithTooltip>
                        </div>
                    </CardBody>
                </Card>
            </div>
        )
    }
}

TrackCard.propTypes = {
    track: PropTypes.object.isRequired,
    permissions: PropTypes.array.isRequired,
    currentUserRole: PropTypes.object.isRequired,
    payrollPeriods: PropTypes.array,
    enableFeedback: PropTypes.bool.isRequired,
    onReviewTrackClick: PropTypes.func.isRequired,
    onApproveClick: PropTypes.func.isRequired,
    currentUserId: PropTypes.string,
    showAbnormalTrackDurationWarning: PropTypes.bool,
    loading: PropTypes.bool,
    deleting: PropTypes.bool,
}

export default TrackCard
